/**
 * Typography-related semantic tokens for PrimeNG preset.
 * Styles with a Figma text style reference its NBX tokens; headings 1, 2 and 4 keep their
 * current values because the snapshot defines Heading/1 and Heading/4 inconsistently.
 */
import { type NbxTokenId, nbxVar } from '../../tokens/generated/nbx-tokens.js';
import * as p from '../tokens/primitives.js';

/** Size, weight, line height and spacing of a Figma text style, keeping the font fallbacks. */
function textStyle(style: string) {
  const token = (prop: string) => nbxVar(`text-style.${style}.${prop}` as NbxTokenId);
  return {
    fontSize: token('font-size'),
    lineHeight: token('line-height'),
    fontWeight: token('font-weight'),
    fontFamily: p.fontFamily.base,
    letterSpacing: token('letter-spacing'),
  };
}

export const typography = {
  // Typography primitives (exposed for PrimeNG semantic tree)
  fontFamily: {
    base: p.fontFamily.base,
  },
  fontSize: p.fontSize,
  fontWeight: {
    regular: p.fontWeight.normal,
    semibold: p.fontWeight.semibold,
    bold: p.fontWeight.bold,
  },
  lineHeight: {
    normal: p.lineHeight.base,
    ...p.lineHeight,
  },

  // Heading styles: H1=32px, H2=24px, H3=18px, H4=16px; all Bold.
  // Mobile-first: tablet+ (≥768px) uses the base values; mobile uses the nested `mobile` values.
  heading: {
    1: {
      fontSize: p.fontSize['3xl'], // 32px (tablet+)
      lineHeight: p.lineHeight['4xl'], // 40px (tablet+)
      fontWeight: p.fontWeight.bold,
      fontFamily: p.fontFamily.base,
      letterSpacing: '0%',
      mobile: {
        fontSize: p.fontSize.xl, // 20px
        lineHeight: p.lineHeight['3xl'], // 32px
      },
    },
    2: {
      fontSize: p.fontSize['2xl'], // 24px (tablet+)
      lineHeight: p.lineHeight['3xl'], // 32px (tablet+)
      fontWeight: p.fontWeight.bold,
      fontFamily: p.fontFamily.base,
      letterSpacing: '0%',
      mobile: {
        fontSize: p.fontSize.lg, // 18px
        lineHeight: p.lineHeight['2xl'], // 28px
      },
    },
    3: textStyle('heading.3'),
    4: {
      fontSize: p.fontSize.base, // 16px
      lineHeight: p.lineHeight.xl, // 24px
      fontWeight: p.fontWeight.bold,
      fontFamily: p.fontFamily.base,
      letterSpacing: '0%',
    },
  },
  body: {
    regular: {
      md: textStyle('body.base'),
      base: textStyle('body.small'),
      s: {
        fontSize: p.fontSize.xs, // 12px
        lineHeight: p.lineHeight.base, // 16px (1.33 ratio)
        fontWeight: p.fontWeight.normal,
        fontFamily: p.fontFamily.base,
        letterSpacing: '0%',
      },
    },
    bold: {
      md: textStyle('body.base-bold'),
      base: textStyle('body.small-bold'),
      s: {
        fontSize: p.fontSize.xs, // 12px
        lineHeight: p.lineHeight.base, // 16px (1.33 ratio)
        fontWeight: p.fontWeight.bold,
        fontFamily: p.fontFamily.base,
        letterSpacing: '0%',
      },
    },
    semibold: {
      base: textStyle('body.base-semi'),
      s: textStyle('body.small-semi'),
    },
  },
  textButton: textStyle('button.label-base'),
  caption: {
    regular: textStyle('body.caption'),
    bold: textStyle('badge.label'),
  },
};
